import { useQuery } from "@apollo/client";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { gql } from "generated-graphql";
import { useState } from "react";

import { AssignedByStaff } from "./AssignedByStaff";

const MANIFEST_REVIEW_SUMMARY_QUERY = gql(`
  query ManifestReviewSummary {
    staffManifestReviewSummary {
      total
      assignedToMe
      unassigned
      unresolved
      overdue
      assignedCounts {
        user {
          id
          email
          person {
            first
            last
          }
        }
        count
      }
    }
  }
`);

interface CountCardProps {
  label: string;
  value: number | undefined;
  loading: boolean;
}

function CountCard({ label, value, loading }: CountCardProps) {
  return (
    <Card variant="outlined" sx={{ minWidth: 140 }}>
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography variant="h4" component="div" fontWeight="bold">
          {loading ? <Skeleton width={40} /> : value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function ManifestReviewSummary() {
  const { data, loading } = useQuery(MANIFEST_REVIEW_SUMMARY_QUERY);
  const summary = data?.staffManifestReviewSummary;
  const assignedCounts = summary?.assignedCounts ?? [];
  const [expanded, setExpanded] = useState(true);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" color="text.secondary">
          Summary
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          aria-label={expanded ? "Collapse summary" : "Expand summary"}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>

      <Collapse in={expanded}>
        {/* Metric cards and the per-reviewer list sit side by side; on small screens they
            stack (column) and the metric cards wrap. */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} mt={1} alignItems="flex-start">
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ flex: { md: 1 }, minWidth: 0 }}>
            <CountCard label="Total" value={summary?.total} loading={loading} />
            <CountCard label="Unassigned" value={summary?.unassigned} loading={loading} />
            <CountCard label="Unresolved" value={summary?.unresolved} loading={loading} />
            <CountCard label="Overdue" value={summary?.overdue} loading={loading} />
            <CountCard label="Assigned to Me" value={summary?.assignedToMe} loading={loading} />
          </Box>
          {assignedCounts.length > 0 && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", md: "block" } }}
            />
          )}
          {assignedCounts.length > 0 && (
            <Box sx={{ flex: { md: 1 }, minWidth: 0 }}>
              <AssignedByStaff assignedCounts={assignedCounts} />
            </Box>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
}
